import { postValidatePhoneCompletionParser, validatePhoneParser } from "../Parsers/Steps/ValidatePhone"
import { loanParser } from "../Parsers/Loan"
import { BankType, InstantLoanStateType, RecentBankInfo } from "../Reducers/types"
import * as ErrorType from "../Parsers/FastTrack/Error"
import * as ReviewType from "../Parsers/FastTrack/Review"
import * as Status from "../Parsers/FastTrack/Status"
import * as PayDate from "../Parsers/FastTrack/PayDate"
import { postMessageSender } from "../PostMessage"
import { PayDatesType } from "../Parsers/FastTrack/PayDate"
import { BankSuggestion } from "../../Components/Steps/BankingDetails/types"
import { ContenfulClient } from "../client-contenful"
import { getRecentlyUsedInstitution, setRecentlyUsedInstitution } from "../recentlyUsedInstitution"

const createTimeoutSignal = (ms: number): { signal: AbortSignal; clear: () => void } => {
  if (typeof AbortSignal !== "undefined" && typeof AbortSignal.timeout === "function") {
    return { signal: AbortSignal.timeout(ms), clear: () => {} }
  }

  const controller = new AbortController()
  const timer = setTimeout(() => {
    // abort(reason) is itself recent; older engines ignore the argument and
    // surface the default AbortError instead of TimeoutError.
    controller.abort(new DOMException(`signal timed out after ${ms}ms`, "TimeoutError"))
  }, ms)

  return { signal: controller.signal, clear: () => clearTimeout(timer) }
}

const getUrl = () => {
  if (import.meta.env.VITE_MARIA_BASE_URL === undefined || import.meta.env.VITE_MARIA_BASE_URL === "") {
    console.warn("import.meta.env.VITE_MARIA_BASE_URL is empty.")
  }
  return import.meta.env.VITE_MARIA_BASE_URL
}

const stringifyFields = (obj: Record<string, any>) => {
  let result: Record<string, string> = {}

  Object.entries(obj).forEach(([key, value]) => {
    result[key] = value.toString()
  })

  return result
}

export const url = getUrl()

export const APPLICATION_ID = "Nt9b58DxJn$nL6bRA7r027JX8cpewoM%O$cf3$t9^NogqiEwR*"

// FIXME: extract token into env
// Maria fetch helper. If body is provided, then it's POST, otherwise GET
export const mariaFetch = async (path: string, token?: string, body?: BodyInit, timeout?: number) => {
  let result
  const signalData = timeout ? createTimeoutSignal(timeout) : undefined
  try {
    result = await fetch(`${url}/${path.slice(path.startsWith("/") ? 1 : 0)}`, {
      method: body ? "POST" : "GET",
      signal: signalData?.signal,
      headers: {
        Authorization: `Bearer ${token}`,
        "Application-ID": APPLICATION_ID,
        ...(body ? { "Content-Type": "application/json" } : {}),
      },
      body,
    })

    if ([401, 403].includes(result.status)) {
      throw (await result.json()).error
    }
  } catch (e: any) {
    // send error/code to parent window
    postMessageSender({
      event: "error",
      error: e,
    })
    throw e
  } finally {
    signalData?.clear();
  }
  return result
}

export const getUserReloadToken = (token?: string) => mariaFetch(`/user/info`, token)

export const getUser = (token?: string) => mariaFetch(`/user/info`, token).then((res) => res.json())

export const getGeneralProvinces = () => mariaFetch(`/general/provinces`).then((res) => res.json())

export const getLoan = (token?: string) =>
  mariaFetch(`/application/loan`, token)
    .then((res) => res.json())
    .then((res) => res.result)

export const getLoanStatus = (token?: string) =>
  mariaFetch(`/user/dashboard/instantloan`, token)
    .then((res) => res.json())
    .then((res) => res.result)

export const postLoan = (state: InstantLoanStateType["loanParameters"], token?: string) =>
  mariaFetch(`/application/loan`, token, loanParser(state))

export type ValidatePhoneResult =
  | { voipDetected: true; message: string }
  | { voipDetected: false }

export const postValidatePhone = async (
  state: InstantLoanStateType["mobileVerification"],
  token?: string,
  voipConfirmed?: boolean
): Promise<ValidatePhoneResult> => {
  const res = await mariaFetch(`/application/validatephone`, token, validatePhoneParser(state, voipConfirmed))
  const json = await res.json()

  if (res.status === 202) {
    return { voipDetected: true, message: json.result?.message || json.message || json.error?.message || "" }
  }

  if (json.error) throw new Error(json.error.message || "error")

  return { voipDetected: false }
}

export const postValidatePhoneCompletion = (
  mobileState: InstantLoanStateType["mobileVerification"],
  pin: number,
  token?: string
) =>
  mariaFetch(`/application/validatephonecompletion`, token, postValidatePhoneCompletionParser(mobileState, pin))
    .then((res) => res.json())
    .then((res) => {
      if (res.error) throw new Error(res.error.message || "error")
      return res
    })
    .then((res) => res.result)

export const getStepRequest = (step: number, token?: string) =>
  mariaFetch(`/application/step${step}`, token)
    .then((res) => res.json())
    .then((res) => {
      if (res.error) throw Object.assign(new Error(res.error.message), res.error)
      return {
        data: stringifyFields(res.result.data || res.result),
        list: res.result.list,
      }
    })

export const postStepRequest = (step: number, body: Record<string, any>, token?: string) =>
  mariaFetch(`/application/step${step}`, token, JSON.stringify(body))
    .then((res) => {
      return res.headers.get("Content-Type")?.includes("json") ? res.json() : undefined
    })
    .then((res) => {
      if (res?.error) {
        if (res.error?.message === "This contact has no active application") {
          //FIX when the application no longer exist.
          postMessageSender({
            event: "return",
          })
        }
        throw new Error(res.error.message || "error")
      }
    })

export const getStep1FastTrack = async (
  token?: string
): Promise<{ error?: ErrorType.ErrorType; data?: ReviewType.ReviewType }> => {
  try {
    const res = await mariaFetch(`/application/fasttrack/step1`, token)
    const json = await res.json()
    if (json.error) {
      const error = ErrorType.Convert.toErrorType(JSON.stringify(json.error))
      return {
        error: error,
      }
    }

    if (json.result) {
      const result = ReviewType.Convert.toReviewType(JSON.stringify(json.result))
      return {
        data: result,
      }
    }
  } catch (error) {}

  return {
    error: {
      code: 0,
      message: "Unknown error.",
    },
  }
}

export const postStep1FastTrack = async (data: { bankEtransfer: number }, token?: string): Promise<boolean> => {
  try {
    const res = await mariaFetch(`/application/fasttrack/step1`, token, JSON.stringify(data))
    if (res.status === 201) {
      return true
    }
  } catch (error) {
    console.warn(error)
  }
  return false
}

export const getStep2FastTrack = async (
  token?: string
): Promise<{ error?: ErrorType.ErrorType; data?: PayDate.PayDatesType }> => {
  try {
    const res = await mariaFetch(`/application/fasttrack/step2`, token)
    const json = await res.json()
    if (json.error) {
      const error = ErrorType.Convert.toErrorType(JSON.stringify(json.error))
      return {
        error: error,
      }
    }

    if (json.result) {
      const result = json.result as PayDatesType
      return {
        data: result,
      }
    }
  } catch (error) {
    console.warn(error)
  }

  return {
    error: {
      code: 0,
      message: "Unknown error.",
    },
  }
}

export const postStep2FastTrack = async (
  data: { lastPayDate?: string; nextPayDate?: string; consent: boolean },
  token?: string
): Promise<{ error?: { message: string; code: number } }> => {
  try {
    const res = await mariaFetch(`/application/fasttrack/step2`, token, JSON.stringify(data))
    if (res.status === 201) {
      return {}
    }
    const json = await res.json()
    if (json.error) {
      return {
        error: json.error,
      }
    }
    console.warn(json)
  } catch (error) {
    console.warn(error)
  }
  return {
    error: {
      code: 0,
      message: "Unknown error.",
    },
  }
}

export const postMarketingGroupSubscribe = async (token?: string) => {
  let result
  try {
    result = await fetch(`${url}/marketinggroup/all/subscribe`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Application-ID": APPLICATION_ID,
        "Content-Type": "application/json",
      },
    })

    if ([401, 403].includes(result.status)) {
      throw (await result.json()).error
    }
  } catch (e: any) {
    // send error/code to parent window
    postMessageSender({
      event: "error",
      error: e,
    })
    throw e
  }
}

export const getStatusFastTrack = async (
  token?: string
): Promise<{
  error?: ErrorType.ErrorType
  data?: Status.FastTrackStatusType
}> => {
  try {
    const res = await mariaFetch(`/application/fasttrack/status`, token)
    const json = await res.json()
    if (json.error) {
      const error = ErrorType.Convert.toErrorType(JSON.stringify(json.error))
      return {
        error: error,
      }
    }

    if (json.result) {
      const result = Status.Convert.toFastTrackStatusType(JSON.stringify(json.result))
      return {
        data: result,
      }
    }
  } catch (error) {}

  return {
    error: {
      code: 0,
      message: "Unknown error.",
    },
  }
}

export const getMarketingConsent = async (
  token?: string
): Promise<{
  error?: ErrorType.ErrorType
  data?: { subscribedAll: boolean }
}> => {
  try {
    const res = await mariaFetch(`/marketinggroup`, token)
    const json = await res.json()
    if (json.error) {
      const error = ErrorType.Convert.toErrorType(JSON.stringify(json.error))
      return {
        error: error,
      }
    }

    if (json.result) {
      const result = json.result as { subscribedAll: boolean }
      return {
        data: result,
      }
    }
  } catch (error) {}

  return {
    error: {
      code: 0,
      message: "Unknown error.",
    },
  }
}

export const getMostPopularInstitutions = async (token?: string, size = 10) => {
  try {
    const res = await mariaFetch(`/alicia/banking-institution?page=1&pageSize=${size}`, token)
    const json = await res.json()

    if (json.error) {
      const error = ErrorType.Convert.toErrorType(JSON.stringify(json.error))
      return {
        error: error,
      }
    }

    return { data: json.institutions as BankType[] }
  } catch (error) {}

  return {
    error: {
      code: 0,
      message: "Unknown error.",
    },
  }
}

export const getRecentUsedInstitutions = async (token?: string, userId?: number) => {
  try {
    const data: BankType[] = getRecentlyUsedInstitution(userId)

    if (data.length > 0) return { data }

    const res = await mariaFetch(`/banking/recentBanks`, token)
    const json = await res.json()

    if (json.error) {
      const error = ErrorType.Convert.toErrorType(JSON.stringify(json.error))
      return {
        error: error,
      }
    }

    if (json.result.length == 0) return { data: [] }

    const recentBanksInfo: RecentBankInfo[] = json.result

    for await (const recentBankInfo of recentBanksInfo) {
      if (recentBankInfo.plaidId) {
        const bankInfo = await getInstitutionById(token, recentBankInfo.plaidId)

        if (bankInfo.data?.enabled) {
          setRecentlyUsedInstitution(bankInfo.data, userId)
          data.push(bankInfo.data)
          break
        }
      }

      if (recentBankInfo.bankNameOrId) {
        const bankInfo = await getInstitutionById(token, recentBankInfo.bankNameOrId)

        if (bankInfo.data?.enabled) {
          setRecentlyUsedInstitution(bankInfo.data, userId)
          data.push(bankInfo.data)
          break
        }
      }

      const banksInfo = await searchInstitutions(recentBankInfo.bankName, token)

      const bankInfo = banksInfo.data?.find((item) => item.nameEn.includes(recentBankInfo.bankName))

      if (bankInfo) {
        setRecentlyUsedInstitution(bankInfo, userId)
        data.push(bankInfo)
        break
      }
    }

    return {
      data,
    }
  } catch (error) {
    console.warn(error)
  }

  return {
    error: {
      code: 0,
      message: "Unknown error.",
    },
  }
}

export const getInstitutionById = async (token?: string, institutionId?: string) => {
  try {
    const res = await mariaFetch(`/alicia/banking-institution/${institutionId}`, token)
    const json = await res.json()

    if (json.error) {
      const error = ErrorType.Convert.toErrorType(JSON.stringify(json.error))
      return {
        error: error,
      }
    }

    return { data: json as BankType }
  } catch (error) {
    console.warn(error)
  }

  return {
    error: {
      code: 0,
      message: "Unknown error.",
    },
  }
}

export const searchAutoSuggestion = async (term: string, token?: string) => {
  try {
    const res = await mariaFetch(`/alicia/banking-institution/auto-suggest-search?term=${term}`, token)
    const json = await res.json()

    if (json.error) {
      const error = ErrorType.Convert.toErrorType(JSON.stringify(json.error))
      return {
        error: error,
      }
    }

    return { data: json.suggestions as BankSuggestion[] }
  } catch (error) {
    console.warn(error)
  }

  return {
    error: {
      code: 0,
      message: "Unknown error.",
    },
  }
}

export const searchInstitutions = async (term: string, token?: string) => {
  try {
    const res = await mariaFetch(`/alicia/banking-institution/search`, token, JSON.stringify({ term }))
    const json = await res.json()

    if (json.error) {
      const error = ErrorType.Convert.toErrorType(JSON.stringify(json.error))
      return {
        error: error,
      }
    }

    return { data: json as BankType[] }
  } catch (error) {
    console.warn(error)
  }

  return {
    error: {
      code: 0,
      message: "Unknown error.",
    },
  }
}
export const postPaymentVerificationAgree = async (
  token?: string,
  applicationId?: string,
  data?: { agreementAccepted: boolean }
): Promise<{ error?: { message: string; code: number } }> => {
  try {
    const res = await mariaFetch(
      `/v3/applications/${applicationId}/payment/verification/agree`,
      token,
      JSON.stringify(data)
    )
    if (res.status === 204 || res.status === 200) {
      return {}
    }
    const json = await res.json()
    if (json.error) {
      return {
        error: json.error,
      }
    }
  } catch (error) {
    console.warn(error)
  }
  return {
    error: {
      code: 0,
      message: "Unknown error.",
    },
  }
}

export const getApplicationReviewMessages = async () => {
  if (!ContenfulClient) {
    console.warn(
      "ContenfulClient is undefined.\nREACT_APP_CONTENTFUL_ACCESS_TOKEN OR REACT_APP_CONTENTFUL_SPACE_ID not set."
    )
    return
  }

  try {
    const res = await ContenfulClient.getEntries({
      content_type: "applicationReviewMessages",
    })

    return Object.values(res.items[0].fields) as string[]
  } catch (error) {
    console.warn(error)
  }
}

// Identity Verification - Initialize Onfido SDK
export const getOnfidoToken = async (
  applicationId: number,
  token?: string
): Promise<{
  error?: { message: string; code: number }
  data?: { sdkToken: string; workflowRunId: string }
}> => {
  try {
    const res = await mariaFetch(
      `/v3/identity-verification/initialize`,
      token,
      JSON.stringify({ applicationId }),
      30_000
    )

    const json = await res.json()

    if (!res.ok || json?.error) {
      return {
        error: {
          code: json?.error?.code ?? res.status,
          message: json?.error?.message ?? "Failed to initialize identity verification",
        },
      }
    }

    // The SDK cannot mount without both values, so surface it as an error instead of
    // handing back a half-empty payload that leaves the caller stuck on its loading state.
    if (!json?.sdkToken || !json?.workflowRunId) {
      return {
        error: {
          code: res.status,
          message: "Identity verification did not return an SDK token",
        },
      }
    }

    return {
      data: {
        sdkToken: json.sdkToken,
        workflowRunId: json.workflowRunId,
      },
    }
  } catch (error) {
    console.warn(error)
    return {
      error: {
        code: 0,
        message: "Unknown error.",
      },
    }
  }
}

// Identity Verification - Get Latest Status
export type IdentityVerificationStatus =
  | "pending"
  | "in_progress"
  | "processing_result"
  | "awaiting_decision"
  | "abandoned"
  | "pending_retry"
  | "manual_review"
  | "approved"
  | "declined"
  | "expired"
  | "withdrawn"

export interface IdentityVerificationResult {
  status: IdentityVerificationStatus
  sdkToken?: string
  id?: number
  contactId?: number
  retryCount?: number
  maxRetryAttempts?: number
  workflowType?: 'document_and_biometric' | 'document_only'
}

export const getLatestIdentityVerification = async (
  token?: string
): Promise<{
  error?: { message: string; code: number }
  data?: IdentityVerificationResult
}> => {
  try {
    const res = await mariaFetch(`/v3/identity-verification/latest`, token)
    const json = await res.json()

    if (json.error) {
      return {
        error: {
          code: json.error.code || 0,
          message: json.error.message || "Failed to get identity verification status",
        },
      }
    }

    return {
      data: json,
    }
  } catch (error) {
    console.warn(error)
    return {
      error: {
        code: 0,
        message: "Unknown error.",
      },
    }
  }
}
